import { isWindowGated, type Channel } from "./types";
import { STANDARD_WINDOW_MS, windowState } from "./window";

/**
 * TA-1371 contacts C3 (ManyChat teardown, Kabir typed "go ahead"): an owner's reminder on a thread, read
 * against the 24h window. ManyChat warns "the reply window might be closed already" when an agent sets a
 * reminder; this is that warning, built on the one reading of the window there is.
 *
 * READ ONLY ON THE T3 MACHINE (CLAUDE.md sections 3 and 8). The reading is core's `windowState`, evaluated
 * at the reminder's instant instead of now. Nothing here restates a threshold, adds a state or grants
 * anything: `grace_by_then` is `grace` (F-01, it grants nothing `in_window` does not) and `closed_by_then` is
 * `closed` (no leniency after 24h, ever).
 *
 * COMPUTED AT RENDER, NEVER STORED. The window moves every time the customer writes, so the caller passes the
 * thread's live `last_customer_message_at` each time it draws the reminder. A note saved with the reminder
 * would be stale the moment the customer wrote again.
 *
 * F-20: a channel the 24h rule does not govern (sms, email, a public comment, a string this build does not
 * know) is `not_applicable`, said out loud, never a bare "open".
 */

export type ReminderWindowNote = "open" | "grace_by_then" | "closed_by_then" | "not_applicable";

export interface ReminderWindowReading {
  readonly note: ReminderWindowNote;
  /** The gate's own close (last inbound plus STANDARD_WINDOW_MS). Null with no inbound, or not applicable. */
  readonly closesAt: Date | null;
}

const NOTE_OF = { in_window: "open", grace: "grace_by_then", closed: "closed_by_then" } as const;

const invalid = (d: Date): boolean => Number.isNaN(d.getTime());

export function reminderWindowNote(input: {
  readonly channel: Channel | string;
  readonly lastCustomerMessageAt: Date | null;
  readonly remindAt: Date;
}): ReminderWindowReading {
  // An invalid instant throws here, loud (CLAUDE.md section 0). Since TA-1377 windowState reads an
  // unreadable clock as closed (`windowClockUnreadable`), never open; a reminder note on such a clock would
  // still be a quiet wrong answer, so the throw stays (TA-1378).
  if (invalid(input.remindAt)) throw new Error("reminderWindowNote: remindAt is not a valid instant");
  if (input.lastCustomerMessageAt !== null && invalid(input.lastCustomerMessageAt)) {
    throw new Error("reminderWindowNote: lastCustomerMessageAt is not a valid instant");
  }
  if (!isWindowGated(input.channel as Channel)) return { note: "not_applicable", closesAt: null };
  const state = windowState({ lastCustomerMessageAt: input.lastCustomerMessageAt, now: input.remindAt });
  const closesAt =
    input.lastCustomerMessageAt === null ? null : new Date(input.lastCustomerMessageAt.getTime() + STANDARD_WINDOW_MS);
  return { note: NOTE_OF[state], closesAt };
}
